import { createSlice } from '@reduxjs/toolkit';
import { merge } from 'lib';
import {
  fetchBulletinExportSettings,
  updateBulletinStatisticsSettings,
} from './actions';

const initialState = {
  updateRequest: {
    loading: false,
    error: null,
  },
  fetchRequest: {
    loading: false,
    error: null,
    synced: false,
  },
  summarizerRequest: true,
  isActive: false,
  queryId: null,
  timeDuration: '1m',
  fromDate: null,
  toDate: null,
  newspapers: {
    isActive: false,
    options: {
      atReportStart: true,
      atReportEnd: false,
    },
  },
  statistics: {
    publicCharts: {
      contentChart: true,
      subjectChart: true,
    },
    charts: {
      wordCloud: {
        instagram: true,
        telegram: true,
        twitter: true,
        virasty: true,
        news: true,
        eitaa: true,
        bale: true,
        rubika: true,
      },
      hashtagCloud: {
        instagram: true,
        telegram: true,
        twitter: true,
        virasty: true,
        eitaa: true,
        bale: true,
        rubika: true,
      },
      contentPublishingProcess: {
        instagram: true,
        telegram: true,
        twitter: true,
        virasty: true,
        news: true,
        eitaa: true,
        bale: true,
        rubika: true,
      },
      postSentiment: {
        instagram: true,
        telegram: true,
        twitter: true,
      },
      fiveTrendsHashtags: {
        instagram: true,
        telegram: true,
        twitter: true,
        virasty: true,
        eitaa: true,
        bale: true,
        rubika: true,
      },
      influenceResourcesByRelatedContent: {
        instagram: true,
        telegram: true,
        twitter: true,
        news: true,
      },
      influenceResourcesByRepublishersCount: {
        twitter: true,
        news: true,
      },
      influenceResourcesByFollowersCount: {
        instagram: true,
        twitter: true,
      },
      influenceResourcesByMentionsCount: {
        twitter: true,
      },
      influenceResourcesByVisitsCount: {
        telegram: true,
      },
      influenceResourcesByMembersCount: {
        telegram: true,
      },
      geoHeatMap: {
        news: true,
        instagram: true,
        twitter: true,
        virasty: true,
        telegram: true,
        eitaa: true,
        bale: true,
        rubika: true,
      },
    },
  },
};

export const bulletinExportSettingsSlice = createSlice({
  name: 'bulletinExportSettingsSlice',
  initialState,
  reducers: {
    changeBulletinPublicCharts: (state, action) => {
      state.statistics.publicCharts[action.payload] =
        !state.statistics.publicCharts[action.payload];
    },
    changeBulletinCharts: (state, action) => {
      const { key, item } = action.payload;
      state.statistics.charts[key][item] = !state.statistics.charts[key][item];
    },
    changeBulletinExportSettings: (state, action) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    changeBulletinExportDateTime: (state, action) => {
      const { fromDate, toDate, timeDuration } = action.payload;
      state.timeDuration = timeDuration;
      state.fromDate = fromDate;
      state.toDate = toDate;
    },
    changeBulletinExportNewspapers: (state, action) => {
      const { optionKey = null } = action.payload || {};

      if (optionKey) {
        Object.keys(state.newspapers.options).forEach((key) => {
          state.newspapers.options[key] = key === optionKey;
        });
      } else {
        state.newspapers.isActive = !state.newspapers.isActive;
      }
    },
    resetBulletinExportSettings: (state) => {
      state.isActive = initialState.isActive;
      state.queryId = initialState.queryId;
      state.timeDuration = initialState.timeDuration;
      state.fromDate = initialState.fromDate;
      state.toDate = initialState.toDate;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBulletinExportSettings.fulfilled, (state, action) => {
        state.statistics.publicCharts = {
          ...initialState?.statistics?.publicCharts,
          ...action.payload?.statistics?.publicCharts,
        };
        delete action.payload?.statistics?.charts?.contentCategory;
        state.statistics.charts = merge(
          {}, // Start with an empty object to ensure immutability
          initialState?.statistics?.charts,
          action.payload?.statistics?.charts
        );
        state.newspapers = {
          ...initialState.newspapers,
          ...action.payload?.newspapers,
          options: {
            ...initialState.newspapers.options,
            ...action.payload?.newspapers?.options,
          },
        };
        state.fetchRequest.loading = false;
        state.fetchRequest.error = null;
        state.fetchRequest.synced = true;
      })
      .addCase(fetchBulletinExportSettings.pending, (state) => {
        state.fetchRequest.loading = true;
        state.fetchRequest.error = null;
        state.fetchRequest.synced = false;
      })
      .addCase(fetchBulletinExportSettings.rejected, (state, action) => {
        state.fetchRequest.loading = false;
        state.fetchRequest.error = action.payload;
        state.fetchRequest.synced = true;
      })
      .addCase(updateBulletinStatisticsSettings.pending, (state) => {
        state.updateRequest.loading = true;
        state.updateRequest.error = null;
      })
      .addCase(updateBulletinStatisticsSettings.rejected, (state, action) => {
        state.updateRequest.loading = false;
        state.updateRequest.error = action.payload;
      })
      .addCase(updateBulletinStatisticsSettings.fulfilled, (state) => {
        state.updateRequest.loading = false;
        state.updateRequest.error = null;
      });
  },
});

export const {
  changeBulletinPublicCharts,
  changeBulletinCharts,
  changeBulletinExportSettings,
  changeBulletinExportDateTime,
  changeBulletinExportNewspapers,
  resetBulletinExportSettings,
} = bulletinExportSettingsSlice.actions;
